import { motion } from "framer-motion";
import { Play, Mail } from "lucide-react";
import { Eyebrow } from "./primitives";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1590602847861-f357a9332bbc?auto=format&fit=crop&w=1800&q=80";

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.15, delayChildren: 0.2 } },
};

const item = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 1, ease: [0.22, 1, 0.36, 1] } },
};

export const Hero = () => (
  <section
    id="top"
    data-testid="hero-section"
    className="relative flex min-h-screen items-center overflow-hidden"
  >
    <div
      className="absolute inset-0 bg-cover bg-center"
      style={{ backgroundImage: `url(${HERO_IMAGE})` }}
      aria-hidden="true"
    />
    <div className="absolute inset-0 bg-black/70" aria-hidden="true" />
    <div
      className="absolute inset-0 bg-gradient-to-t from-noir via-transparent to-noir/60"
      aria-hidden="true"
    />

    <motion.div
      variants={container}
      initial="hidden"
      animate="show"
      className="relative z-10 mx-auto w-full max-w-6xl px-6 pb-24 pt-40"
    >
      <motion.div variants={item}>
        <Eyebrow>A podcast by Mel Masry</Eyebrow>
      </motion.div>

      <motion.h1
        variants={item}
        data-testid="hero-headline"
        className="mt-6 max-w-4xl font-display text-5xl font-black leading-[1.05] tracking-tight text-bone sm:text-7xl"
      >
        How film shapes
        <br />
        the way <span className="italic text-ruby">we love.</span>
      </motion.h1>

      <motion.p
        variants={item}
        data-testid="hero-subline"
        className="mt-8 max-w-xl text-base font-light leading-relaxed text-zinc-400 sm:text-lg"
      >
        Cinema doesn't just reflect relationships — it shapes expectations. Each
        episode takes one scene you know by heart and asks: is this real, or is
        this something we've been taught to expect?
      </motion.p>

      <motion.div variants={item} className="mt-12 flex flex-wrap items-center gap-4">
        <a
          href="#latest"
          data-testid="hero-listen-button"
          className="flex items-center gap-3 bg-ruby px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-ruby-bright"
        >
          <Play size={14} /> Listen to the pilot
        </a>
        <a
          href="#letters"
          data-testid="hero-ask-button"
          className="flex items-center gap-3 border border-bone/20 px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-bone transition-all duration-300 hover:border-ruby hover:text-ruby"
        >
          <Mail size={14} /> Ask the show
        </a>
      </motion.div>
    </motion.div>

    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1.4, duration: 1 }}
      className="absolute bottom-8 left-6 z-10 hidden items-center gap-3 sm:flex"
      aria-hidden="true"
    >
      <span className="h-px w-16 bg-ruby" />
      <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-smoke">
        Film · Relationships · Psychology · Culture
      </span>
    </motion.div>
  </section>
);
